import React from 'react';

import { formatDate, formatMoney, toCentsFromString, toMoney } from '@betterment/core';
import { ParsedData, StepProps, track } from '@betterment/js-runtime';
import { Icon, Text } from '@betterment/samba-react';
import { Button, CenteredWorkflowLayout, Flex, HR, ListRow, Tooltip } from '@betterment/style-closet-react';
import { Form, Formik, FormikHelpers } from 'formik';

import { DepositValues } from './data';

import { useAccount } from '../Checking/components/AccountProvider';
import { Customer, TransferAvailabilityEstimate } from '../Checking/data';
import { applyApiErrorsToFormik } from '../Checking/form-helpers';
import { intoBettermentAccount } from '../Checking/models/account';
import { intoBankAccount } from '../Checking/models/external-account';
import { AccountOption } from '../Shared/components/AccountOption';
import FlashErrorMessage from '../Shared/components/FlashErrorMessage';
import Footer from '../Shared/components/Footer';
import { WorkflowCard } from '../Shared/components-v2/workflow-card/WorkflowCard';

interface Props extends StepProps<DepositValues> {
  customer: Customer;
  availabilityEstimate: TransferAvailabilityEstimate;
  onDeposit: (values: DepositValues) => Promise<ParsedData<{}>>;
}

const DepositFlowConfirm: React.FC<React.PropsWithChildren<Props>> = ({
  customer,
  availabilityEstimate,
  flowState,
  goToNextStep,
  goToPreviousStep,
  onDeposit,
}) => {
  const { account } = useAccount();

  if (!customer.externalAccount) return null;

  const formattedAmount = formatMoney(toMoney(toCentsFromString(flowState.values.amount)));
  const estimatedDate = availabilityEstimate && formatDate(availabilityEstimate.estimatedDate, 'longOrdinalWithDay');

  const handleSubmit = async (values: DepositValues, formik: FormikHelpers<DepositValues>) => {
    let result;
    try {
      result = await onDeposit({ amount: flowState.values.amount, externalAccountId: values.externalAccountId });
    } finally {
      formik.setSubmitting(false);
    }

    if (result.errors) {
      applyApiErrorsToFormik(formik, result.errors);

      const didIncludeTransactionLimitError: boolean = (result.errors.messages.amountCents || [])
        .join(', ')
        .includes('must be between');
      if (didIncludeTransactionLimitError) {
        track('ElementViewed', { name: 'TransactionLimitReached', flow: 'DepositToChecking' });
      }
    } else {
      goToNextStep();
    }
  };

  return (
    <CenteredWorkflowLayout heading="Review your deposit" handleBackButton={goToPreviousStep}>
      <CenteredWorkflowLayout.Slot name="content">
        <Formik initialValues={flowState.values} onSubmit={handleSubmit}>
          {({ isSubmitting }) => (
            <Form>
              <FlashErrorMessage name="base" />
              <FlashErrorMessage name="amount" />
              <WorkflowCard variant="compact" css={{ marginBottom: 'space.300' }}>
                <ListRow leadingTitle="From">
                  <ListRow.Slot name="trailing">
                    <AccountOption
                      account={intoBankAccount(customer.externalAccount!)}
                      iconSize={8}
                      showBalance={false}
                    />
                  </ListRow.Slot>
                </ListRow>
                <HR variant="list-row" />
                <ListRow leadingTitle="To">
                  <ListRow.Slot name="trailing">
                    <AccountOption
                      account={intoBettermentAccount(account, customer)}
                      iconSize={8}
                      showBalance={false}
                    />
                  </ListRow.Slot>
                </ListRow>
                <HR variant="list-row" />
                <ListRow leadingTitle="Amount" trailingTitle={formattedAmount} />
              </WorkflowCard>
              <HR variant="workflow-card" />
              <WorkflowCard css={{ marginBottom: 'space.150' }}>
                <Flex alignItems="flex-start">
                  <Flex.Item mr="4">
                    <Icon name="Calendar" size="small" color="icon" />
                  </Flex.Item>
                  <Flex.Item>
                    <Text
                      as="p"
                      css={{
                        lineHeight: 'lineHeight.30',
                        marginBottom: 'space.100',
                      }}
                    >
                      Estimated availability
                    </Text>
                    <Text
                      as="p"
                      color="text.weak"
                      css={{
                        fontSize: 'fontSize.50',
                        marginBottom: 'space.200',
                        _last: { marginBottom: 'space.0' },
                      }}
                    >
                      Your funds should be available by {estimatedDate}.{' '}
                      <Tooltip
                        label={`Deposits take about 4–5 business days to complete. This allows for the
                    transaction to process and ensures there are enough funds in your
                    bank account to complete the deposit.`}
                        title="More info"
                        verticalAlign="top"
                      >
                        <Icon name="QuestionMarkCircle" size="small" color="icon.weak" />
                      </Tooltip>
                    </Text>
                  </Flex.Item>
                </Flex>
              </WorkflowCard>
              <Text
                color="text.weak"
                css={{
                  fontSize: 'fontSize.50',
                }}
              >
                Clicking &quot;Submit deposit&quot; will authorize an ACH credit to your Checking account.
              </Text>
              <CenteredWorkflowLayout.Actions>
                <Button type="submit" disabled={isSubmitting}>
                  Submit deposit
                </Button>
              </CenteredWorkflowLayout.Actions>
            </Form>
          )}
        </Formik>
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default DepositFlowConfirm;
